import { useEffect, useState } from 'react'

import { TOAST_ICONS, ToastType } from 'types/toasts'
import { useTransition } from 'hooks/useTransitions'

import ToastService from 'services/toast.service'

import Icon from 'components/elements/Icon'
import Modal from 'components/elements/Modal'

import styles from './toast.module.scss'

type Props = {
  toast: ToastType
  matchesExistingToast?: boolean
}

const Toast: React.FC<Props> = ({ toast, matchesExistingToast }: Props) => {
  const [onScreen, setOnScreen] = useState(true)
  const [ref, status] = useTransition(onScreen, { transitionInOnMount: true, exitingClassName: styles.toastExiting })

  useEffect(() => {
    if (status === 'hidden') ToastService.delete(toast.id)
  }, [status])

  useEffect(() => {
    if (toast.type === 'success') setTimeout(() => setOnScreen(false), toast.duration || 3000)
  }, [])

  useEffect(() => {
    if (matchesExistingToast) setTimeout(() => setOnScreen(false), 1000)
  }, [matchesExistingToast])

  const Body = toast.body

  let message: string | null = toast.text

  try {
    // @ts-expect-error
    const parsedText = JSON.parse(message)

    if (Array.isArray(parsedText)) message = parsedText.join(' / ')
    if (parsedText.detail) message = parsedText.detail
    if (parsedText.detail && parsedText.statusCode === 429) message = formatThrottleMessage(parsedText.detail)
    if (parsedText.message) message = parsedText.message
    if (parsedText.errors) message = parsedText.errors

    // prettier-ignore
    if (parsedText.non_field_errors && Array.isArray(parsedText.non_field_errors) && parsedText.non_field_errors.length) message = parsedText.non_field_errors.join(' / ')
  } catch (err) {
    message = toast.text
  }

  return (
    <div ref={ref} className={`${styles.toast} ${styles[toast.type]}`}>
      <Icon name={TOAST_ICONS[toast.type]} />

      {(toast.title || message) && (
        <div className={styles.info}>
          <h4>{toast.title}</h4>
          <p>{message}</p>

          {toast.type !== 'success' && (
            <Modal trigger="View" triggerClassName={styles.viewDetailsButton} closeButton>
              <div>
                <h3>{toast.title}</h3>
                <p>{toast.text}</p>
              </div>
            </Modal>
          )}
        </div>
      )}

      {!!Body && <Body id={toast.id} {...toast.extraProps} />}

      <button className={styles.closeButton} onClick={() => setOnScreen(false)}>
        <Icon name="remove" />
      </button>
    </div>
  )
}

export default Toast

const formatThrottleMessage = (message: string) => {
  const secondsRemain = message.replace(/\D/g, '')

  return 'Request throttled. Available in ' + secondsRemain + ' seconds.'
}
